import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Send, BookOpen, Loader2 } from 'lucide-react';
import { AppLang } from '../i18n';
import './SupportChatPage.css';

const API_BASE = '/app-api'; // support chat

interface SupportMessage {
  role: 'user' | 'admin';
  text: string;
  created_at: string;
}

interface SupportChatPageProps {
  lang: AppLang;
  onBack: () => void;
  onShowOnboarding?: () => void;
}

function formatTime(created_at: string): string {
  try {
    const d = new Date(created_at.includes('T') ? created_at : created_at.replace(' ', 'T') + 'Z');
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return created_at.slice(11, 16);
  }
}

function formatDate(created_at: string): string {
  try {
    const d = new Date(created_at.includes('T') ? created_at : created_at.replace(' ', 'T') + 'Z');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  } catch {
    return '';
  }
}

function isSameDay(a: string, b: string): boolean {
  try {
    const da = new Date(a.includes('T') ? a : a.replace(' ', 'T') + 'Z');
    const db = new Date(b.includes('T') ? b : b.replace(' ', 'T') + 'Z');
    return da.toDateString() === db.toDateString();
  } catch {
    return false;
  }
}

const TypingDots = () => (
  <div className="sc-typing-dots">
    <span /><span /><span />
  </div>
);

export const SupportChatPage = ({ lang, onBack, onShowOnboarding }: SupportChatPageProps) => {
  const isEn = lang === 'en';
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [ticketStatus, setTicketStatus] = useState<'open' | 'closed'>('open');
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [adminTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastCountRef = useRef(0);

  const getInitData = () => window.Telegram?.WebApp?.initData || '';

  const fetchMessages = useCallback(async (silent = false) => {
    const initData = getInitData();
    if (!initData) return;
    try {
      const res = await fetch(`${API_BASE}/support/messages`, {
        headers: { Authorization: `tma ${initData}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const msgs: SupportMessage[] = data.messages || [];
      setMessages(msgs);
      setTicketStatus(data.status || 'open');
      // Haptic on new admin reply
      if (!silent && msgs.length > lastCountRef.current) {
        const newMsgs = msgs.slice(lastCountRef.current);
        const hasAdminReply = newMsgs.some(m => m.role === 'admin');
        if (hasAdminReply) {
          (window.Telegram?.WebApp as any)?.HapticFeedback?.notificationOccurred('success');
        }
      }
      lastCountRef.current = msgs.length;
    } catch {
      // silent fail on poll
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchMessages(false).then(() => setLoading(false));
  }, [fetchMessages]);

  // Poll every 4s
  useEffect(() => {
    pollRef.current = setInterval(() => fetchMessages(true), 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: messages.length < 3 ? 'instant' : 'smooth' });
  }, [messages, adminTyping]);

  const sendMessage = async () => {
    const text = inputText.trim();
    if (!text || sending || ticketStatus === 'closed') return;
    const initData = getInitData();
    if (!initData) return;

    setInputText('');
    setSending(true);
    setError('');

    // Optimistic update
    const optimistic: SupportMessage = {
      role: 'user',
      text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    lastCountRef.current += 1;

    try {
      const res = await fetch(`${API_BASE}/support/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `tma ${initData}`,
        },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'send_failed');
      }
      // Reload to get server-stamped message
      await fetchMessages(true);
    } catch (e: any) {
      setError(isEn ? 'Failed to send. Try again.' : 'Не удалось отправить. Попробуйте снова.');
      // Rollback optimistic
      setMessages(prev => prev.filter(m => m !== optimistic));
      lastCountRef.current -= 1;
      setInputText(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isClosed = ticketStatus === 'closed';

  return (
    <div className="sc-page">
      {/* Header */}
      <div className="sc-header">
        <button className="sc-back" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <div className="sc-header-info">
          <div className="sc-header-title">
            {isEn ? 'Support chat' : 'Чат поддержки'}
          </div>
          <div className={`sc-header-status ${isClosed ? 'closed' : 'open'}`}>
            <span className="sc-status-dot" />
            {isClosed
              ? (isEn ? 'Closed' : 'Закрыт')
              : (isEn ? 'Online · usually reply in 15 min' : 'Онлайн · отвечаем за 15 мин')}
          </div>
        </div>
        {!!onShowOnboarding && (
          <button className="sc-guide-btn" onClick={onShowOnboarding} title={isEn ? 'Guide' : 'Инструкция'}>
            <BookOpen size={18} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="sc-messages">
        {loading ? (
          <div className="sc-loader">
            <Loader2 size={28} className="sc-spin" />
          </div>
        ) : messages.length === 0 ? (
          <motion.div
            className="sc-empty"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="sc-empty-icon">💬</div>
            <div className="sc-empty-text">
              {isEn
                ? "Write your question — we'll reply quickly!"
                : 'Напишите вопрос — ответим быстро!'}
            </div>
            <div className="sc-empty-hint">
              {isEn
                ? "Tip: include device model and what isn't working"
                : 'Совет: укажите модель устройства и в чём проблема'}
            </div>
          </motion.div>
        ) : (
          <AnimatePresence initial={false}>
            {messages.map((msg, i) => {
              const showDate = i === 0 || !isSameDay(messages[i - 1].created_at, msg.created_at);
              return (
                <motion.div key={i}>
                  {showDate && (
                    <div className="sc-date-divider">
                      <span>{formatDate(msg.created_at)}</span>
                    </div>
                  )}
                  <motion.div
                    className={`sc-msg-row ${msg.role}`}
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.18 }}
                  >
                    {msg.role === 'admin' && (
                      <div className="sc-avatar">⚓</div>
                    )}
                    <div className="sc-bubble-wrap">
                      {msg.role === 'admin' && (
                        <div className="sc-sender-name">
                          {isEn ? 'Support' : 'Поддержка'}
                        </div>
                      )}
                      <div className={`sc-bubble ${msg.role}`}>
                        <span className="sc-bubble-text">{msg.text}</span>
                        <span className="sc-bubble-time">{formatTime(msg.created_at)}</span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}

        {adminTyping && (
          <div className="sc-msg-row admin">
            <div className="sc-avatar">⚓</div>
            <div className="sc-bubble-wrap">
              <div className="sc-bubble admin sc-bubble--typing">
                <TypingDots />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            className="sc-error"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input */}
      <div className={`sc-input-bar ${isClosed ? 'disabled' : ''}`}>
        {isClosed ? (
          <div className="sc-closed-notice">
            {isEn ? 'Ticket closed. Write again to reopen.' : 'Тикет закрыт. Напишите снова, чтобы открыть новый.'}
          </div>
        ) : (
          <>
            <textarea
              ref={inputRef}
              className="sc-input"
              placeholder={isEn ? 'Message…' : 'Сообщение…'}
              value={inputText}
              onChange={e => {
                setInputText(e.target.value);
                setError('');
                // Auto-resize
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={handleKeyDown}
              rows={1}
              maxLength={4000}
              disabled={sending}
            />
            <button
              className={`sc-send-btn ${!inputText.trim() || sending ? 'disabled' : ''}`}
              onClick={sendMessage}
              disabled={!inputText.trim() || sending}
            >
              {sending
                ? <Loader2 size={18} className="sc-spin" />
                : <Send size={18} />}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
